import queryString from 'query-string';
import { apiUrl, authHeaders } from './auth';
import { ApplicationCompletionStatus } from '~/types/application-completion';
import { DisbursementsResponse } from '~/types/disbursements';
import type { DonationDetail } from '~/components/Contributions/drawers/DonationDetailDrawer';

// Medical Professional Update
export const updateApplicationMedicalProfessional = async (
    campaignRequestId: number,
    data: any
) => {
    const headers = authHeaders();
    const url =
        apiUrl + `/admin/campaignRequests/${campaignRequestId}/medicalInfo`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers: {
            ...headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(
            `Error updating medical professional: ${resp.status} - ${errorText}`
        );
    }

    return await resp.json();
};

export const updateApplicationInsuranceInfo = async (
    campaignRequestId: number,
    data: any
) => {
    const headers = authHeaders();
    const url =
        apiUrl + `/admin/campaignRequests/${campaignRequestId}/insuranceInfo`;

    console.log('Insurance API URL:', url);
    console.log('Insurance API Data:', data);

    const resp = await fetch(url, {
        method: 'PUT',
        headers: {
            ...headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        console.error('Insurance API Error Response:', errorText);
        console.error('Response status:', resp.status);
        throw new Error(
            `Error updating insurance info: ${resp.status} - ${errorText}`
        );
    }

    return await resp.json();
};

export const updateApplicationInsuranceInfoViaApplication = async (
    applicationId: number,
    data: any
) => {
    const headers = authHeaders();
    const url =
        apiUrl + `/admin/campaignApplications/${applicationId}/insuranceInfo`;

    console.log('Insurance API URL (via Application):', url);
    console.log('Insurance API Data:', data);

    const resp = await fetch(url, {
        method: 'PUT',
        headers: {
            ...headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        console.error('Insurance API Error Response:', errorText);
        console.error('Response status:', resp.status);
        throw new Error(
            `Error updating insurance info: ${resp.status} - ${errorText}`
        );
    }

    return await resp.json();
};

export const updateApplicationFinancialInfo = async (
    campaignRequestId: number,
    data: any
) => {
    const headers = authHeaders();
    const url =
        apiUrl + `/admin/campaignRequests/${campaignRequestId}/financialInfo`;

    console.log('Financial API URL:', url);
    console.log('Financial API Data:', data);

    const resp = await fetch(url, {
        method: 'PUT',
        headers: {
            ...headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        console.error('Financial API Error Response:', errorText);
        console.error('Response status:', resp.status);
        throw new Error(
            `Error updating financial info: ${resp.status} - ${errorText}`
        );
    }

    return await resp.json();
};

export const updateApplicationAgreements = async (
    applicationId: number,
    data: any
) => {
    const headers = authHeaders();
    const url =
        apiUrl + `/admin/campaignApplications/${applicationId}/agreements`;

    console.log('Agreements API URL:', url);
    console.log('Agreements API Data:', data);

    const resp = await fetch(url, {
        method: 'PUT',
        headers: {
            ...headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    if (!resp.ok) {
        const errorText = await resp.text();
        console.error('Agreements API Error Response:', errorText);
        console.error('Response status:', resp.status);
        throw new Error(
            `Error updating agreements: ${resp.status} - ${errorText}`
        );
    }

    return await resp.json();
};

// Service endpoints to support admin fuctionality

export const fetchCampaignSearchFilters = async () => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/campaignSearchFilters';
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchCampaigns = async (
    params?: any,
    filters?: any,
    dateRange?: any,
    page = 1
) => {
    const searchParams = queryString.stringify(params, {
        skipNull: true,
        skipEmptyString: true,
    });
    const filterParams = queryString.stringify(filters, {
        skipNull: true,
        skipEmptyString: true,
    });
    console.log('filterParams: ', filterParams);
    const dateRangeParams = queryString.stringify(dateRange, {
        skipNull: true,
        skipEmptyString: true,
    });
    const pageNumber = queryString.stringify(
        { page },
        { skipNull: true, skipEmptyString: true }
    );

    const search = [searchParams, filterParams, dateRangeParams, pageNumber]
        .filter((param) => !!param)
        .join('&');
    const headers = authHeaders();
    const url = apiUrl + '/admin/campaigns?' + search;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    return await resp.json();
};

export type NonClientCampaignTypeOption = {
    id: number;
    name: string;
    code: string;
    is_patient_campaign: boolean;
};

export const fetchNonClientCampaignTypes = async (): Promise<
    NonClientCampaignTypeOption[]
> => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/campaigns/non-client/types';
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(
            (err as { detail?: string }).detail || 'Error loading fund types'
        );
    }
    return await resp.json();
};

export type CreateNonClientCampaignPayload = {
    name: string;
    cf_patient_ntaf_id: string;
    campaign_type_code: string;
    financial_coordinator_person_id?: number | null;
};

export const createNonClientCampaign = async (
    payload: CreateNonClientCampaignPayload
) => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/campaigns/non-client';
    const resp = await fetch(url, {
        method: 'POST',
        headers: {
            ...headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    if (!resp.ok) {
        const detail = json?.detail;
        const message =
            typeof detail === 'string'
                ? detail
                : detail
                ? JSON.stringify(detail)
                : 'Could not create campaign';
        throw new Error(message);
    }
    return json as { campaign_id: number; cf_patient_ntaf_id: string };
};

export const fetchCampaignDonorCounts = async (ntaf_ids: string[] = []) => {
    if (ntaf_ids.length === 0) return [];
    const params = ntaf_ids
        .filter((x) => typeof x === 'string' && x.length > 0)
        .join(',');
    const headers = authHeaders();
    const url = apiUrl + '/admin/campaigns/donorCounts?ntaf_ids=' + params;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchAssignees = async () => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/campaigns/assignees';
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchCampaign = async (id: any) => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/campaigns/' + id;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    console.log('json: ', json);
    return json;
};

// View-only disbursement report for a given campaign. Reuses the v2
// disbursements endpoint, which honors a campaign_id override for staff/admin
// users (see campaign.utils.get_campaign).
export const fetchDisbursements = async (
    campaignId: string | number,
    startDate: string | null = null,
    endDate: string | null = null
): Promise<DisbursementsResponse> => {
    const headers = authHeaders();
    let url = apiUrl + `/v2/myDisbursements?campaign_id=${campaignId}`;
    if (startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    const resp = await fetch(url, { method: 'GET', headers });
    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(
            `Error fetching disbursements: ${resp.status} - ${errorText}`
        );
    }
    return await resp.json();
};

export const fetchMessages = async (
    params: any,
    dateRange: any,
    page = 1,
    name = '',
    assignee = ''
) => {
    const headers = authHeaders();
    if (params?.sortBy === 'isPinned') {
        params.sortBy = 'pinned_flag';
    }
    if (params?.sortBy === 'type') {
        params.sortBy = 'todo_flag';
    }
    if (params?.sortBy === 'date') {
        params.sortBy = 'created_date';
    }
    const searchParams = queryString.stringify(params, { skipNull: true });
    const dateRangeParams = queryString.stringify(dateRange, {
        skipNull: true,
    });
    const pageNumber = queryString.stringify({ page }, { skipNull: true });
    const nameParams = queryString.stringify({ name }, { skipNull: true });
    const assigneeParams = queryString.stringify(
        { assignee },
        { skipNull: true }
    );

    const search = [
        searchParams,
        dateRangeParams,
        pageNumber,
        nameParams,
        assigneeParams,
    ]
        .filter((param) => !!param)
        .join('&');

    const url = `${apiUrl}/admin/messages?${search}`;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const deleteMessage = async (id: number) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/messages/${id}`;
    return await fetch(url, {
        method: 'DELETE',
        headers,
    });
};

export const updateMessage = async (id: number, payload: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/messages/${id}`;
    return await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
};

export const postMessage = async (payload: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/messages`;
    const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Error posting message');
    const json = await res.json();
    return json;
};

export const fetchNoteEditHistory = async (
    campaignId: number,
    messageId: number
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/notes/${messageId}/history`;
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error('Error fetching note edit history');
    const json = await res.json();
    return json.results;
};

export const fetchSupportersCount = async (ntaf_id: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${ntaf_id}/supporters/count`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchSupporters = async (
    ntaf_id: string,
    page = 1,
    payload: any
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${ntaf_id}/supporters?page=${page}`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export type CampaignRefundRow = {
    refund_id: number;
    lookup_id: string;
    /** The donation row this refund came out of, for opening its drawer. Null
     *  once a full refund has deleted it. */
    donation_id: number | null;
    name: string;
    email: string;
    address: string;
    ntaf_id: string;
    campaign_id: number | null;
    campaign_name: string;
    is_full_refund: boolean;
    donation_refunded: string;
    tip_refunded: string;
    total_refunded: string;
    original_amount: string;
    remaining_amount: string;
    refunded_at: string | null;
    refunded_by: string;
    gift_date: string | null;
    stripe_charge_id: string;
    stripe_refund_id: string;
    payout_id: string;
    payment_method: string;
    status: 'pending' | 'succeeded' | 'failed';
    failure_reason: string;
    completion_status: 'pending' | 'complete';
    completed_at: string | null;
    completed_by: string;
    /** Who last flipped the completion flag, either direction — read from the
     *  audit log, so it survives a reopen (which clears completed_by). */
    completion_changed_by: string;
    completion_changed_at: string | null;
};

export type RefundTotals = {
    donation_refunded: string;
    tip_refunded: string;
    total_refunded: string;
    succeeded_count: number;
    pending_count: number;
    failed_count: number;
};

export const fetchCampaignRefunds = async (
    ntaf_id: string,
    page = 1,
    payload: any
): Promise<{ count: number; results: CampaignRefundRow[] }> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${ntaf_id}/refunds?page=${page}`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        throw new Error('Failed to load refunds');
    }
    return resp.json();
};

export const exportSupporters = async (ntaf_id: string, payload: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${ntaf_id}/supporters/export`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        throw new Error('Failed to export supporters');
    }
    return resp.blob();
};

export const deleteSupporter = async (id: number) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/supporters/${id}`;
    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });
    if (!resp.ok && resp.status !== 204) {
        let message = 'Failed to delete supporter record';
        try {
            const data = await resp.json();
            if (data?.detail) message = data.detail;
        } catch {
            // ignore parse errors
        }
        throw new Error(message);
    }
};

export interface UpdateSupporterAnonymityResponse {
    id: number;
    donor_is_anonymous: number;
    donor_share_amount: number;
    nxt_updated: boolean;
}

export const updateSupporterAnonymity = async (
    id: number,
    fields: { donor_is_anonymous?: boolean; donor_share_amount?: boolean }
): Promise<UpdateSupporterAnonymityResponse> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/supporters/${id}/anonymity`;
    const resp = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(fields),
    });
    if (!resp.ok) {
        let message = 'Failed to update anonymity';
        try {
            const data = await resp.json();
            if (data?.detail) message = data.detail;
        } catch {
            // ignore parse errors
        }
        throw new Error(message);
    }
    return resp.json();
};

export interface UpdateDonationFields {
    donor_is_anonymous?: boolean;
    donor_share_amount?: boolean;
    ntaf_id?: string;
    campaign_id?: number | null;
    gift_code?: string;
}

export interface UpdateDonationResponse {
    id: number;
    ntaf_id: string;
    campaign_id: number | null;
    gift_code: string;
    donor_is_anonymous: number;
    donor_share_amount: number;
    nxt_updated: boolean;
}

export const updateDonation = async (
    id: number,
    fields: UpdateDonationFields
): Promise<UpdateDonationResponse> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/supporters/${id}/edit`;
    const resp = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(fields),
    });
    if (!resp.ok) {
        let message = 'Failed to update donation';
        try {
            const data = await resp.json();
            if (data?.detail) message = data.detail;
        } catch {
            // ignore parse errors
        }
        throw new Error(message);
    }
    return resp.json();
};

// Fetch a single donation's full detail (same shape as a supporter-list row),
// used to open the Donation Detail drawer from outside the contributions list
// (e.g. a gift row on the Payout Detail page).
export const getDonationDetail = async (
    id: number
): Promise<DonationDetail> => {
    const headers = authHeaders();
    const resp = await fetch(`${apiUrl}/admin/supporters/${id}`, { headers });
    if (!resp.ok) throw new Error('Failed to load donation');
    return resp.json();
};

export interface DonationEditHistoryEntry {
    id: number;
    changed_at: string;
    changed_by: string;
    old_ntaf_id: string;
    new_ntaf_id: string;
    old_campaign_id: number | null;
    new_campaign_id: number | null;
    old_campaign_name: string;
    new_campaign_name: string;
    old_gift_code: string;
    new_gift_code: string;
    nxt_recreated: boolean;
}

export const getDonationEditHistory = async (
    id: number
): Promise<DonationEditHistoryEntry[]> => {
    const headers = authHeaders();
    const resp = await fetch(
        `${apiUrl}/admin/supporters/${id}/edit-history`,
        { headers }
    );
    if (!resp.ok) throw new Error('Failed to load edit history');
    const data = await resp.json();
    return data.results ?? [];
};

export const fetchAllSupporters = async (page = 1, payload: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/supporters?page=${page}`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    return json;
};

export type AllRefundsFilters = {
    from?: string;
    to?: string;
    search?: string;
    ntaf_id?: string;
    status?: string;
    completion_status?: string;
    /** Refunds-table column id; unknown columns fall back to newest-first. */
    sort_by?: string;
    sort_dir?: string;
};

/** Refunds across every campaign — the "Refunds" view of One Time Contributions. */
export const fetchAllRefunds = async (
    page = 1,
    payload: AllRefundsFilters
): Promise<{
    count: number;
    results: CampaignRefundRow[];
    totals: RefundTotals;
}> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/supporters/refunds?page=${page}`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.detail || 'Failed to load refunds');
    }
    return resp.json();
};

export const exportAllSupporters = async (payload: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/supporters/export`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.detail || 'Failed to export supporters');
    }
    return resp.blob();
};

export const createDonation = async (ntaf_id: string, payload: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${ntaf_id}/supporters/add`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    if (!resp.ok) {
        throw new Error(json.detail || 'Failed to create donation');
    }
    return json;
};

export const createTeamMember = async (payload: any) => {
    const headers = authHeaders();
    const campaign_id = payload.campaign_id;
    
    // Use new admin endpoint for team member creation
    const baseUrl = apiUrl + '/admin/teammembers';
    const url = campaign_id ? `${baseUrl}?campaign_id=${campaign_id}` : baseUrl;
    
    // Remove campaign_id from payload as it's now in the query string
    const { campaign_id: _, ...payloadWithoutCampaignId } = payload;
    
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payloadWithoutCampaignId),
    });
    const json = await resp.json();
    return json;
};

export const putTeamMember = async (payload: any) => {
    const headers = authHeaders();
    const campaign_id = payload.campaign_id;
    const team_member_id = payload.id;
    
    // Use new admin endpoint for team member updates
    const baseUrl = apiUrl + `/admin/teammembers/${team_member_id}`;
    const url = campaign_id ? `${baseUrl}?campaign_id=${campaign_id}` : baseUrl;
    
    // Remove campaign_id and id from payload as they're now in the URL/query
    const { campaign_id: _, id: __, ...payloadWithoutIds } = payload;
    
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payloadWithoutIds),
    });
    const json = await resp.json();
    return json;
};

export const deleteTeamMember = async (id: number, campaign_id?: string) => {
    const headers = authHeaders();
    const baseUrl = apiUrl + '/v2/teammember/' + id;
    const url = campaign_id ? `${baseUrl}?campaign_id=${campaign_id}` : baseUrl;
    const resp = await fetch(url, {
        method: 'DELETE',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const toggleTeamMemberActive = async (
    teamMemberId: number,
    campaignId: string | number,
    isActive: boolean
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/teammembers/${teamMemberId}?campaign_id=${campaignId}`;
    const resp = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ is_active: isActive }),
    });
    const json = await resp.json();
    return json;
};

export const createTeamMemberAccount = async (teamMemberId: number) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/teammembers/${teamMemberId}/create-account`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
    });
    if (!resp.ok) {
        const json = await resp.json();
        throw new Error(json.detail || 'Error creating user profile');
    }
    return await resp.json();
};

export const fetchCampaignMetadata = async () => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/campaigns/metadata';
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchRecentCampaigns = async (state?: string) => {
    const headers = authHeaders();
    const params = state ? `?state=${encodeURIComponent(state)}` : '';
    const url = apiUrl + '/admin/recentCampaigns' + params;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchApplicationsForFinalReview = async () => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/applicationsForFinalReview';
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const searchCampaigns = async (searchTerm: any, hasNtafId?: boolean) => {
    const headers = authHeaders();
    let url = apiUrl + '/admin/quickSearchCampaigns?search=' + searchTerm;

    // Add has_ntaf_id parameter if specified
    if (hasNtafId) {
        url += '&has_ntaf_id=true';
    }

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const searchCampaignsAdvanced = async (filters: any, page = 1) => {
    const headers = authHeaders();
    // Add page parameter to filters
    const filtersWithPage = { ...filters, page };
    const searchParams = queryString.stringify(filtersWithPage, {
        skipNull: true,
        skipEmptyString: true,
        // Removed arrayFormat to use default repeated parameter format: campaign_status=funded&campaign_status=disbursable
    });
    const url = `${apiUrl}/admin/searchCampaigns?${searchParams}`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchMyCampaignRequests = async (pageSize: number = 5) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests?assigned_to_me=true&pageSize=${pageSize}`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Failed to fetch my campaign requests');
    }
    const json = await resp.json();
    return json;
};

export const fetchCampaignRequests = async (
    params: any,
    filters: any,
    dateRange: any,
    page = 1
) => {
    const searchParams = queryString.stringify(params, {
        skipNull: true,
        skipEmptyString: true,
    });
    const filterParams = queryString.stringify(filters, {
        skipNull: true,
        skipEmptyString: true,
    });
    const dateRangeParams = queryString.stringify(dateRange, {
        skipNull: true,
        skipEmptyString: true,
    });
    const pageNumber = queryString.stringify(
        { page },
        { skipNull: true, skipEmptyString: true }
    );
    const search = [searchParams, filterParams, dateRangeParams, pageNumber]
        .filter((param) => !!param)
        .join('&');

    const headers = authHeaders();
    const url = apiUrl + '/admin/campaignRequests?' + search;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const fetchCampaignRequestContactAttemptsTotal = async (
    filters: any,
    dateRange: any
): Promise<{ total: number }> => {
    const filterParams = queryString.stringify(filters, {
        skipNull: true,
        skipEmptyString: true,
    });
    const dateRangeParams = queryString.stringify(dateRange, {
        skipNull: true,
        skipEmptyString: true,
    });
    const search = [filterParams, dateRangeParams]
        .filter((param) => !!param)
        .join('&');

    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/contactAttemptsTotal?${search}`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Failed to fetch contact attempts total');
    }
    return await resp.json();
};

export const fetchCampaignRequest = async (id: any) => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/campaignRequests/' + id;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

/**
 * Search campaign requests with advanced filters
 * Supports filtering by status, date range, name, etc.
 */
export const searchCampaignRequests = async (
    filters: Record<string, any>,
    page = 1
) => {
    const headers = authHeaders();
    
    // Build query string from filters
    const params = new URLSearchParams();
    
    // Add page
    params.append('page', page.toString());
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            if (Array.isArray(value)) {
                // Handle multiple values (e.g., multiple statuses)
                value.forEach(v => params.append(key, v));
            } else {
                params.append(key, String(value));
            }
        }
    });
    
    const url = `${apiUrl}/admin/campaignRequests?${params.toString()}`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    
    if (!resp.ok) {
        console.error('searchCampaignRequests failed:', resp.status, resp.statusText);
        throw new Error('Failed to search campaign requests');
    }
    
    const json = await resp.json();
    return json;
};

export const putPatient = async (payload: any) => {
    const headers = authHeaders();
    const url = apiUrl + '/v2/patient';
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || 'Failed to update patient information');
        error.detail = errorData.detail;
        throw error;
    }
    const json = await resp.json();
    return json;
};

export const putCampaignRequestPatient = async (
    campaignRequestId: number,
    payload: any
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/patient`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || errorData.error || 'Failed to update patient information');
        error.detail = errorData.detail || errorData.error;
        throw error;
    }
    const json = await resp.json();
    return json;
};

export const putCampaignApplicationPatient = async (
    applicationId: number,
    payload: any
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignApplications/${applicationId}/patient`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || 'Failed to update patient information');
        error.detail = errorData.detail;
        throw error;
    }
    const json = await resp.json();
    return json;
};

export const putCampaignRequestInitialContact = async (
    campaignRequestId: number,
    payload: any
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/initialContact`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || errorData.error || 'Failed to update initial contact information');
        error.detail = errorData.detail || errorData.error;
        throw error;
    }
    const json = await resp.json();
    return json;
};

export const putCampaignRequestReferredBy = async (
    campaignRequestId: number,
    payload: any
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/referredBy`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || errorData.error || 'Failed to update referred by source');
        error.detail = errorData.detail || errorData.error;
        throw error;
    }
    const json = await resp.json();
    return json;
};

export const putCampaignRequestPrimaryContact = async (
    campaignRequestId: number,
    payload: any
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/primaryContact`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || errorData.error || 'Failed to update primary contact information');
        error.detail = errorData.detail || errorData.error;
        throw error;
    }
    const json = await resp.json();
    return json;
};

export const putCampaignRequestMedicalInfo = async (
    campaignRequestId: number,
    payload: any
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/medicalInfo`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        throw new Error('Failed to update medical information');
    }
    const json = await resp.json();
    return json;
};

export const fetchMedicalProfessionals = async (search?: string) => {
    const headers = authHeaders();
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const url = `${apiUrl}/admin/medicalProfessionals${params}`;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Failed to fetch medical professionals');
    }
    const json = await resp.json();
    return json;
};

// Team Member report search (HHLTB-2800). Repeated params for multi-selects,
// matching how the backend reads them with getlist().
export const searchTeamMembers = async (
    filters: Record<string, any>,
    page = 1
) => {
    const headers = authHeaders();
    const params = new URLSearchParams();
    params.append('page', page.toString());

    Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            if (Array.isArray(value)) {
                value.forEach(v => params.append(key, v));
            } else {
                params.append(key, String(value));
            }
        }
    });

    const url = `${apiUrl}/admin/searchTeamMembers?${params.toString()}`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });

    if (!resp.ok) {
        console.error('searchTeamMembers failed:', resp.status, resp.statusText);
        throw new Error('Failed to search team members');
    }

    return await resp.json();
};

export const fetchStates = async () => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/states';
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    const json = await resp.json();
    return json;
};

export const exportCampaignSearchResults = async (filters: any) => {
    const headers = authHeaders();
    const searchParams = queryString.stringify(filters, {
        skipNull: true,
        skipEmptyString: true,
        // Removed arrayFormat to use default repeated parameter format: campaign_status=funded&campaign_status=disbursable
    });
    const url = `${apiUrl}/admin/exportCampaignSearchResults?${searchParams}`;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });

    if (!resp.ok) {
        throw new Error('Failed to export campaign search results');
    }

    // Get the filename from the Content-Disposition header
    const contentDisposition = resp.headers.get('Content-Disposition');
    let filename = 'campaign_search_results.csv';
    if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
            filename = match[1];
        }
    }

    // Get the blob data
    const blob = await resp.blob();

    // Create download link
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true, filename };
};

export const fetchPatientServicesCoordinators = async (searchTerm?: string) => {
    const headers = authHeaders();
    let url = apiUrl + '/admin/patientServicesCoordinators';
    if (searchTerm) {
        url += `?search=${encodeURIComponent(searchTerm)}`;
    }
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Failed to fetch Client Services Coordinators');
    }
    const json = await resp.json();
    return json;
};

export const assignPSCToCampaignRequest = async (
    campaignRequestId: number,
    pscId: number
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/assignPSC`;
    const resp = await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ patient_services_coordinator_id: pscId }),
    });
    if (!resp.ok) {
        throw new Error('Failed to assign Client Services Coordinator');
    }
    const json = await resp.json();
    return json;
};

export const assignCampaignCSC = async (
    campaignId: string | number,
    pscId: number
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/assignCsc`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ psc_id: pscId }),
    });
    if (!resp.ok) {
        throw new Error('Failed to assign Client Services Coordinator');
    }
    const json = await resp.json();
    return json;
};

export const updateCampaignStatus = async (
    campaignId: string | number,
    statusCode: string
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/status`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: statusCode }),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update campaign status');
    }
    const json = await resp.json();
    return json;
};

export const updateCampaignFundraising = async (
    campaignId: string | number,
    payload: { therm_start_date?: string | null; goal?: number }
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/fundraising`;
    const resp = await fetch(url, {
        method: 'PATCH',
        headers: {
            ...headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(
            errorData.error || errorData.detail || 'Failed to update fundraising info'
        );
    }
    return await resp.json();
};

export const setCampaignWPStatus = async (
    campaignId: string | number,
    wpStatus: 'publish' | 'draft'
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/wp-status`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ status: wpStatus }),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update WordPress status');
    }
    return await resp.json();
};

export const publishCampaignToWP = async (campaignId: string | number) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/wp-publish`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to publish campaign to WordPress');
    }
    return await resp.json();
};

export const fetchCampaignRequestPossibleMatches = async (
    campaignRequestId: number
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/possibleMatches`;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Failed to fetch possible campaign matches');
    }
    const json = await resp.json();
    return json;
};

export const approveCampaignRequest = async (
    campaignRequestId: number,
    comments: string
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/approve`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ comments }),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const errorMessage = errorData.error || 'Failed to approve campaign request';
        throw new Error(errorMessage);
    }
    const json = await resp.json();
    return json;
};

export const approveCampaignApplication = async (
    applicationId: number,
    comments: string
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignApplications/${applicationId}/approve`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ comments }),
    });
    if (!resp.ok) {
        const errorText = await resp.text();
        throw new Error(`Failed to approve campaign application: ${errorText}`);
    }
    const json = await resp.json();
    return json;
};

export const rejectCampaignRequest = async (campaignRequestId: number) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/reject`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const errorMessage = errorData.error || 'Failed to reject campaign request';
        throw new Error(errorMessage);
    }
    const json = await resp.json();
    return json;
};

export const createCampaignRequestNote = async (
    campaignRequestId: number,
    noteData: {
        note: string;
        assignment?: number | null;
        todo_flag?: boolean;
    }
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/notes`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(noteData),
    });
    if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(
            errorData.error || 'Failed to create campaign request note'
        );
    }
    const json = await resp.json();
    return json;
};

export const updateCampaignRequestNote = async (
    campaignRequestId: number,
    noteId: number,
    payload: any
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/notes/${noteId}`;
    return await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
    });
};

export const updateCampaignRequestNotesField = async (
    campaignRequestId: number,
    notes: string
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/requestNotes`;
    return await fetch(url, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ notes }),
    });
};

export const deleteCampaignRequestNote = async (
    campaignRequestId: number,
    noteId: number
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/notes/${noteId}`;
    return await fetch(url, {
        method: 'DELETE',
        headers,
    });
};

export const fetchCampaignRequestNoteEditHistory = async (
    campaignRequestId: number,
    noteId: number
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/notes/${noteId}/history`;
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error('Error fetching note edit history');
    const json = await res.json();
    return json.results;
};

export const fetchCampaignApplications = async (
    params: any,
    filters: any,
    dateRange: any,
    page = 1
) => {
    const searchParams = queryString.stringify(params, {
        skipNull: true,
        skipEmptyString: true,
    });
    const filterParams = queryString.stringify(filters, {
        skipNull: true,
        skipEmptyString: true,
    });
    const dateRangeParams = queryString.stringify(dateRange, {
        skipNull: true,
        skipEmptyString: true,
    });
    const pageNumber = queryString.stringify(
        { page },
        { skipNull: true, skipEmptyString: true }
    );
    const search = [searchParams, filterParams, dateRangeParams, pageNumber]
        .filter((param) => !!param)
        .join('&');

    const headers = authHeaders();
    const url = apiUrl + '/admin/campaignApplications?' + search;

    console.log('Fetching campaign applications from:', url);

    const resp = await fetch(url, {
        method: 'get',
        headers,
    });

    const json = await resp.json();

    if (!resp.ok) {
        console.error('Campaign Applications API Error:', json);
        const error = new Error(
            json.message || 'Failed to fetch campaign applications'
        );
        (error as any).response = { data: json, status: resp.status };
        throw error;
    }

    return json;
};

export const fetchCampaignApplication = async (applicationId: number) => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/campaignApplications/' + applicationId;
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });

    if (!resp.ok) {
        throw new Error('Failed to fetch campaign application');
    }

    const json = await resp.json();
    console.log('Campaign Application Detail:', json);
    return json;
};

export const fetchCampaignApplicationTeamMembers = async (applicationId: number) => {
    const headers = authHeaders();
    const url = apiUrl + '/admin/campaignApplications/' + applicationId + '/teamMembers';
    const resp = await fetch(url, {
        method: 'get',
        headers,
    });

    if (!resp.ok) {
        throw new Error('Failed to fetch campaign application team members');
    }

    const json = await resp.json();
    return json;
};

export const createCampaignRequest = async (payload: any) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaignRequests`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    const json = await resp.json();
    if (!resp.ok) {
        const error: any = new Error(
            json.detail || json.error || json.message || 'Failed to create campaign request'
        );
        error.response = {
            status: resp.status,
            data: json,
        };
        throw error;
    }
    return json;
};

export const fetchCampaignApplicationCompletionStatus = async (
    campaignId: number
): Promise<ApplicationCompletionStatus> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/application/completion-status`;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Failed to fetch application completion status');
    }
    const json = await resp.json();
    return json;
};

export const validateCampaignApplication = async (
    campaignId: number
): Promise<ApplicationCompletionStatus> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/application/completion-status`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Failed to validate application');
    }
    const json = await resp.json();
    return json;
};

export const fetchCampaignActivityLog = async (
    campaignId: number | string,
    filter: string = 'all',
    page: number = 1,
    pageSize: number = 20,
    includeAll: boolean = false
) => {
    const headers = authHeaders();
    const params = queryString.stringify({
        filter,
        page,
        page_size: pageSize,
        include_all: includeAll,
    });
    const url = `${apiUrl}/admin/campaigns/${campaignId}/activity-log?${params}`;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Failed to fetch activity log');
    }
    const json = await resp.json();
    return json;
};

export const fetchCampaignRequestActivityLog = async (
    campaignRequestId: number | string,
    page: number = 1,
    pageSize: number = 20
) => {
    const headers = authHeaders();
    const params = queryString.stringify({ page, page_size: pageSize });
    const url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/activity-log?${params}`;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Failed to fetch activity log');
    }
    const json = await resp.json();
    return json;
};

export const fetchCampaignNotes = async (campaignId: number | string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/notes`;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Failed to fetch campaign notes');
    }
    const json = await resp.json();
    return json;
};

export const fetchCampaignMipBalance = async (
    ntafId: string
): Promise<{ total: number }> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/available-balance/${ntafId}`;
    const resp = await fetch(url, { method: 'GET', headers });
    if (!resp.ok) {
        throw new Error('Failed to fetch MIP balance');
    }
    return await resp.json();
};

export const setMedicalProfessionalViaCampaign = async (
    campaignId: number,
    data: {
        medCenterId: number;
        personId: number;
        consent: boolean;
        certifyIdentity: number;
        signature: string;
    }
) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/set-medical-professional`;
    const resp = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to set medical professional');
    }
    return await resp.json();
};

export const resendProfessionalEmail = async (campaignId: number) => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/resend-professional-email`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const error: any = new Error(
            errorData.error || 'Failed to resend professional email'
        );
        error.detail = errorData.error;
        throw error;
    }
    return await resp.json();
};

export const refreshCampaignMipBalance = async (
    ntafId: string
): Promise<{ total: number; last_updated: string; refreshed: boolean }> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/fundrequests/refresh-balance/${ntafId}`;
    const resp = await fetch(url, { method: 'POST', headers });
    if (!resp.ok) {
        throw new Error('Failed to refresh MIP balance');
    }
    return await resp.json();
};

// Letters

export const fetchLetterTemplates = async (
    campaignId: number | string
): Promise<{ id: number; name: string }[]> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/letter-templates`;
    const resp = await fetch(url, { method: 'GET', headers });
    if (!resp.ok) {
        throw new Error('Failed to fetch letter templates');
    }
    return await resp.json();
};

export const downloadMergedDocx = async (
    campaignId: number | string,
    letterId: number,
    roleIds: number[]
): Promise<void> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/letters/merged-docx`;
    const resp = await fetch(url, {
        method: 'POST',
        headers: {
            ...headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ letter_id: letterId, role_ids: roleIds }),
    });
    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to download merged document');
    }
    const blob = await resp.blob();
    const disposition = resp.headers.get('Content-Disposition');
    const filenameMatch = disposition?.match(/filename="(.+)"/);
    const filename = filenameMatch ? filenameMatch[1] : 'Letters.docx';
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
};

export const assignNxtFund = async (
    campaignId: number | string,
    nxtFundId: string
): Promise<{ nxt_fund_id: string }> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/assign-nxt-fund`;
    console.log('[assignNxtFund] POST', url, { nxt_fund_id: nxtFundId });
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ nxt_fund_id: nxtFundId }),
    });
    if (!resp.ok) {
        const rawBody = await resp.text().catch(() => '');
        let parsed: any = {};
        try {
            parsed = rawBody ? JSON.parse(rawBody) : {};
        } catch {
            parsed = {};
        }
        console.error('[assignNxtFund] FAILED', {
            status: resp.status,
            statusText: resp.statusText,
            url,
            body: rawBody,
        });
        const detail =
            parsed.detail ||
            parsed.message ||
            parsed.error ||
            rawBody.slice(0, 300) ||
            'Failed to assign NXT fund';
        throw new Error(`[${resp.status}] ${detail}`);
    }
    return await resp.json();
};

export const createNxtFund = async (
    campaignId: number | string,
    overrides: { description?: string; goal?: number } = {}
): Promise<{ nxt_fund_id: string; ntaf_id: string; region_value: string | null }> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/create-nxt-fund`;
    console.log('[createNxtFund] POST', url, overrides);
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(overrides),
    });
    if (!resp.ok) {
        const rawBody = await resp.text().catch(() => '');
        let parsed: any = {};
        try {
            parsed = rawBody ? JSON.parse(rawBody) : {};
        } catch {
            parsed = {};
        }
        console.error('[createNxtFund] FAILED', {
            status: resp.status,
            statusText: resp.statusText,
            url,
            body: rawBody,
        });
        const detail =
            parsed.detail ||
            parsed.message ||
            parsed.error ||
            rawBody.slice(0, 300) ||
            'Failed to create NXT fund';
        throw new Error(`[${resp.status}] ${detail}`);
    }
    return await resp.json();
};

export const clearNxtFund = async (
    campaignId: number | string
): Promise<{ nxt_fund_id: null }> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/clear-nxt-fund`;
    const resp = await fetch(url, { method: 'POST', headers });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to unlink NXT fund');
    }
    return await resp.json();
};

export const updateCampaignNtafId = async (
    campaignId: number | string,
    ntafId: string
): Promise<{ ntaf_id: string }> => {
    const headers = authHeaders();
    const url = `${apiUrl}/admin/campaigns/${campaignId}/update-ntaf-id`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ntaf_id: ntafId }),
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update NTAF ID');
    }
    return await resp.json();
};
